import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import LockIcon from "@mui/icons-material/Lock";
import {
  getAllBatchesViaAdmin,
  previewRollResequence,
  commitRollResequence,
} from "../../../utils/Api";

/**
 * Roll Number Reconcile.
 *
 * Roll numbers are minted at admission, so they encode ADMISSION ORDER, not alphabetical order.
 * Intakes arrive in waves — the first wave is everyone whose fees cleared, then stragglers as
 * payments land — and each wave appends to the end. A batch admitted over three waves is
 * alphabetical only within each wave.
 *
 * Appending is the right default: a number already given to a student must never silently
 * change. This screen is the deliberate "admissions are closed, now put the register in order"
 * action, and it is intentionally a separate, explicit step rather than something that happens
 * automatically.
 *
 * It is guarded. Once exam results, attendance, or fee receipts reference a roll number, that
 * number is a join key and renumbering orphans those records. The server counts every such
 * reference and refuses unless the admin explicitly overrides.
 */
const RollNumberReconcile = () => {
  const [batches, setBatches] = useState([]);
  const [batchName, setBatchName] = useState("");
  const [rollStart, setRollStart] = useState("");
  const [assignGrNumbers, setAssignGrNumbers] = useState(false);
  const [grStart, setGrStart] = useState("");

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceAcknowledged, setForceAcknowledged] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { batches: all } = await getAllBatchesViaAdmin();
        setBatches((all || []).map((b) => b.batchName).filter(Boolean).sort());
      } catch {
        toast.error("Could not load batches.");
      }
    })();
  }, []);

  const loadPreview = useCallback(async () => {
    if (!batchName) return;
    setLoading(true);
    try {
      const data = await previewRollResequence({
        batchName,
        rollStart: rollStart === "" ? null : Number(rollStart),
        assignGrNumbers,
        grStart: grStart === "" ? null : Number(grStart),
      });
      setPlan(data);
    } catch (e) {
      setPlan(null);
      toast.error(e?.response?.data?.message || "Could not build the preview.");
    } finally {
      setLoading(false);
    }
  }, [batchName, rollStart, assignGrNumbers, grStart]);

  useEffect(() => {
    if (!batchName) {
      setPlan(null);
      return;
    }
    const t = setTimeout(loadPreview, 350);
    return () => clearTimeout(t);
  }, [batchName, rollStart, assignGrNumbers, grStart, loadPreview]);

  const unsafe = plan && !plan.safety?.safe;

  const doCommit = async () => {
    setCommitting(true);
    try {
      const res = await commitRollResequence({
        batchName,
        rollStart: rollStart === "" ? null : Number(rollStart),
        assignGrNumbers,
        grStart: grStart === "" ? null : Number(grStart),
        force: unsafe && forceAcknowledged,
      });
      toast.success(res.message);
      setConfirmOpen(false);
      setForceAcknowledged(false);
      loadPreview();
    } catch (e) {
      const data = e?.response?.data;
      toast.error(data?.message || "Resequence failed.");
    } finally {
      setCommitting(false);
    }
  };

  const changed = plan?.moves?.filter((m) => m.changed) || [];

  return (
    <div className="p-6">
      <Typography variant="h5" gutterBottom>
        Roll Number Reconcile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Re-sorts a batch into surname order and rewrites roll numbers as one contiguous run. Use
        this once admissions for a batch are finished. Students admitted in later waves are
        appended at the end, so their numbers will not be alphabetical until you run this.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel id="rr-batch">Batch / Class</InputLabel>
            <Select
              labelId="rr-batch"
              label="Batch / Class"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
            >
              {batches.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Leave blank to re-order within the range the batch already occupies.">
            <TextField
              size="small"
              label="Start roll from"
              placeholder="auto"
              value={rollStart}
              onChange={(e) => setRollStart(e.target.value.replace(/\D/g, ""))}
              sx={{ width: 170 }}
            />
          </Tooltip>

          <FormControlLabel
            control={
              <Checkbox checked={assignGrNumbers} onChange={(e) => setAssignGrNumbers(e.target.checked)} />
            }
            label="Also resequence GR numbers"
          />
          {assignGrNumbers && (
            <TextField
              size="small"
              label="Start GR from"
              placeholder="auto"
              value={grStart}
              onChange={(e) => setGrStart(e.target.value.replace(/\D/g, ""))}
              sx={{ width: 160 }}
            />
          )}

          {loading && <CircularProgress size={20} />}
        </Box>
      </Paper>

      {plan && (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
            <Chip label={`${plan.totalCount} student(s)`} />
            <Chip
              color={plan.changedCount ? "warning" : "success"}
              label={plan.changedCount ? `${plan.changedCount} will change` : "already in order"}
            />
            {plan.rollRange && <Chip variant="outlined" label={`Roll ${plan.rollRange[0]} – ${plan.rollRange[1]}`} />}
            {plan.grRange && <Chip variant="outlined" label={`GR ${plan.grRange[0]} – ${plan.grRange[1]}`} />}
          </Box>

          {plan.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warnings</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {plan.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}

          {unsafe ? (
            <Alert severity="error" icon={<LockIcon />} sx={{ mb: 2 }}>
              <AlertTitle>
                These roll numbers are already in use by {plan.safety.totalReferences} record(s)
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Roll numbers are used as a key by the records below. Renumbering now would break
                the link between a student and their results, attendance or receipts.
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {plan.safety.dependencies.map((d, i) => (
                  <li key={i}>
                    {d.label}: {d.count} record(s) — keyed by {d.keyedBy}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              Safe to reconcile — no exam, attendance or fee record references these roll numbers
              yet.
            </Alert>
          )}

          {plan.totalCount > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Surname</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>App No</TableCell>
                    <TableCell>Roll Number</TableCell>
                    <TableCell>GR Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.moves.map((m, i) => (
                    <TableRow key={String(m.studentId)} hover sx={m.changed ? { backgroundColor: "#fffbeb" } : undefined}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{m.surname}</TableCell>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>{m.applicationNumber ?? "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {m.oldRollNumber === m.newRollNumber ? (
                          m.newRollNumber
                        ) : (
                          <>
                            <span style={{ color: "#94a3b8", textDecoration: "line-through" }}>
                              {m.oldRollNumber}
                            </span>
                            <ArrowRightAltIcon sx={{ fontSize: 16, verticalAlign: "middle", mx: 0.5 }} />
                            <strong>{m.newRollNumber}</strong>
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {String(m.oldGrNumber ?? "") === String(m.newGrNumber ?? "") ? (
                          (m.newGrNumber ?? "—")
                        ) : (
                          <>
                            <span style={{ color: "#94a3b8", textDecoration: "line-through" }}>
                              {m.oldGrNumber || "—"}
                            </span>
                            <ArrowRightAltIcon sx={{ fontSize: 16, verticalAlign: "middle", mx: 0.5 }} />
                            <strong>{m.newGrNumber}</strong>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              disabled={!plan.changedCount || plan.blocked || committing}
              onClick={() => setConfirmOpen(true)}
              sx={{ backgroundColor: "#15803d", "&:hover": { backgroundColor: "#166534" } }}
            >
              Apply to {changed.length} student(s)
            </Button>
            {plan.blocked && (
              <Typography variant="body2" color="error">
                Blocked — resolve the warnings above first.
              </Typography>
            )}
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm reconcile</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <p>
              This will change the roll number of <strong>{changed.length}</strong> student(s) in{" "}
              <strong>{batchName}</strong>
              {assignGrNumbers ? ", and their GR numbers," : ""} to put the batch in surname
              order.
            </p>
            <p>
              Students who have already been told their roll number will need to be informed of
              the change.
            </p>
          </DialogContentText>

          {unsafe && (
            <>
              <Divider sx={{ my: 2 }} />
              <Alert severity="error" sx={{ mb: 1 }}>
                {plan.safety.totalReferences} existing record(s) reference these roll numbers.
                Proceeding will break those links.
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceAcknowledged}
                    onChange={(e) => setForceAcknowledged(e.target.checked)}
                  />
                }
                label="I understand and want to renumber anyway"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={committing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={unsafe ? "error" : "primary"}
            disabled={committing || (unsafe && !forceAcknowledged)}
            onClick={doCommit}
          >
            {committing ? "Applying…" : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RollNumberReconcile;
